import { ReactEventHandler } from "react";

/**
 * Takes a function that may be undefined and calls it with the given arguments
 * and returns the result when the function is not undefined, or returns
 * `undefined` if the function is undefined.
 */
export const maybeCall = <A extends unknown[] = unknown[], B = unknown>(
  fn?: (...args: A) => B,
  ...args: A
): B | undefined => {
  return fn !== undefined ? fn(...args) : undefined;
};

/**
 * Calls `fn` if `value` is not null and returns the result, otherwise return
 * `undefined` if `value` is null or undefined.
 */
export function apply<A, B>(
  value: A | null | undefined,
  fn: (arg: A) => B
): B | undefined {
  return value == null ? undefined : fn(value);
}

export function applyN<A, B>(
  value: A | null | undefined,
  fn: (arg: A) => B
): B | null {
  return value == null ? null : fn(value);
}

/**
 * Wraps an event handler function to stop propagation.
 */
export function stopPropagation<E extends ReactEventHandler>(handler: E): E {
  return ((e) => {
    e.stopPropagation();

    return handler(e);
  }) as E;
}
